import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { routerNavigatedAction } from '@ngrx/router-store';
import { Action, Store } from '@ngrx/store';
import { isEqual } from 'lodash-es';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, take, throttleTime, withLatestFrom } from 'rxjs/operators';

import { ConversationActions } from './conversation.actions';
import { ConversationSelectors } from './conversation.selectors';
import { ConversationService } from '../../features/comment/services/conversation.service';
import { Permission } from '../../features/shared/models/api/permissions.enum';
import { PermissionService } from '../../features/shared/services/permission.service';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { AppActions } from '../app/app.actions';
import { RootState } from '../root-state.model';

@Injectable()
export class ConversationEffects {
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly service = inject(ConversationService);
  private readonly permissionService = inject(PermissionService);

  navigationAutoLoadEffect: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(routerNavigatedAction),
      map(() => window.location.pathname),
      distinctUntilChanged(),
      map(() => ConversationActions.RefreshConversationCounts()),
    ),
  );

  autoLoadEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ConversationActions.RefreshConversationCounts),
      withLatestFrom(this.permissionService.hasAnyPermission([Permission.READ_COMMENT])),
      filter(([action, permission]) => !!permission),
      map(() => this.service.getContextLocationByRoute(window.location.pathname)),
      filter((location) => !!location),
      distinctUntilChanged(),
      switchMap((location) =>
        this.store.select(ConversationSelectors.anchorsByContextLocation(location)).pipe(
          distinctUntilChanged((a, b) => isEqual(a, b)),
          withLatestFrom(this.store),
          map(([anchors, store]) => {
            return {
              anchors: anchors?.anchors,
              reset: anchors?.reset,
            };
          }),
          filter(({ anchors }) => !!anchors?.length),
          debounceTime(500),
          distinctUntilChanged((a, b) => isEqual(a, b)),
          map(({ anchors, reset }) => ConversationActions.DownloadConversationCounts({ anchors, reset })),
          reportEffectError(ConversationActions.DownloadConversationCountsFail),
        ),
      ),
    ),
  );

  wsSubscription: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(routerNavigatedAction),
      map((action) => this.service.getContextLocationByRoute(window.location.pathname)),
      filter((location) => !!location),
      distinctUntilChanged(),
      switchMap((location) =>
        // Awaited, not sampled: `permissions$` withholds its first emission until the profile
        // loads, so a `withLatestFrom` here would drop a deep-link navigation.
        this.permissionService.hasAnyPermission([Permission.READ_COMMENT]).pipe(
          filter((hasPermission) => hasPermission),
          take(1),
          switchMap(() =>
            this.service.listenOnChanges$().pipe(
              withLatestFrom(this.store.select(ConversationSelectors.anchorsByContextLocation(location))),
              map(([anchor, anchors]) => (anchors?.anchors?.find((a) => a.id === anchor) ? anchors?.anchors : undefined)),
              filter((anchors) => !!anchors),
              throttleTime(50, undefined, { leading: true, trailing: false }),
              map((anchors) => ConversationActions.DownloadConversationCounts({ anchors })),
              reportEffectError(ConversationActions.DownloadConversationCountsFail),
            ),
          ),
        ),
      ),
    ),
  );

  autoClearEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(routerNavigatedAction, ConversationActions.RefreshConversationCounts),
      map((action) => this.service.getContextLocationByRoute(window.location.pathname)),
      filter((location) => !!location),
      distinctUntilChanged(),
      map(() => ConversationActions.ClearConversationCounts()),
    ),
  );

  loadLabels$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ConversationActions.DownloadConversationCounts),
      switchMap(({ anchors }) => {
        return this.service.getConversationAnchorSummaries(anchors).pipe(
          map((anchoredConversation) =>
            ConversationActions.DownloadConversationCountsSuccess({
              anchoredConversation,
              loadedIdentifiers: anchors.map((a) => `${a.type}/${a.id}`),
            }),
          ),
          reportEffectError(ConversationActions.DownloadConversationCountsFail),
        );
      }),
    ),
  );

  addPendingConversation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ConversationActions.AddPendingConversation),
      map(() => AppActions.ToggleNotesPanel({ open: true })),
    ),
  );
}
